/**
 * `when` expression evaluator.
 *
 * The grammar is deliberately tiny — `!`, `&&`, `||`, parentheses, and operands
 * drawn from a closed vocabulary. There is no property access, no literal, no
 * function call, and no `eval`. A template that needs more than this is trying
 * to express a business rule, which is the host's job.
 *
 * Grammar (lowest to highest precedence):
 *   or   := and ( "||" and )*
 *   and  := unary ( "&&" unary )*
 *   unary:= "!" unary | primary
 *   prim := "(" or ")" | IDENT
 */

export class ExpressionError extends Error {}

type Token = { kind: "ident" | "op"; value: string };

const IDENT = /^[A-Za-z][A-Za-z0-9_.]*/;

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let rest = source;

  while (rest.length > 0) {
    if (/^\s/.test(rest)) {
      rest = rest.slice(1);
      continue;
    }
    if (rest.startsWith("&&") || rest.startsWith("||")) {
      tokens.push({ kind: "op", value: rest.slice(0, 2) });
      rest = rest.slice(2);
      continue;
    }
    if (rest[0] === "!" || rest[0] === "(" || rest[0] === ")") {
      tokens.push({ kind: "op", value: rest[0] });
      rest = rest.slice(1);
      continue;
    }
    const match = rest.match(IDENT);
    if (!match) {
      throw new ExpressionError(`Unexpected character '${rest[0]}' in expression: ${source}`);
    }
    tokens.push({ kind: "ident", value: match[0] });
    rest = rest.slice(match[0].length);
  }

  return tokens;
}

type Ast =
  | { n: "ident"; name: string }
  | { n: "not"; operand: Ast }
  | { n: "and"; left: Ast; right: Ast }
  | { n: "or"; left: Ast; right: Ast };

function parse(source: string): Ast {
  const tokens = tokenize(source);
  let index = 0;

  const peek = () => tokens[index];
  const eat = (value: string) => {
    if (peek()?.value !== value) {
      throw new ExpressionError(`Expected '${value}' in expression: ${source}`);
    }
    index += 1;
  };

  function parsePrimary(): Ast {
    const token = peek();
    if (!token) throw new ExpressionError(`Unexpected end of expression: ${source}`);
    if (token.value === "(") {
      eat("(");
      const inner = parseOr();
      eat(")");
      return inner;
    }
    if (token.kind !== "ident") {
      throw new ExpressionError(`Expected an identifier, got '${token.value}' in: ${source}`);
    }
    index += 1;
    return { n: "ident", name: token.value };
  }

  function parseUnary(): Ast {
    if (peek()?.value === "!") {
      index += 1;
      return { n: "not", operand: parseUnary() };
    }
    return parsePrimary();
  }

  function parseAnd(): Ast {
    let node = parseUnary();
    while (peek()?.value === "&&") {
      index += 1;
      node = { n: "and", left: node, right: parseUnary() };
    }
    return node;
  }

  function parseOr(): Ast {
    let node = parseAnd();
    while (peek()?.value === "||") {
      index += 1;
      node = { n: "or", left: node, right: parseAnd() };
    }
    return node;
  }

  const ast = parseOr();
  if (index !== tokens.length) {
    throw new ExpressionError(`Trailing input '${tokens[index].value}' in expression: ${source}`);
  }
  return ast;
}

const cache = new Map<string, Ast>();

function compile(source: string): Ast {
  const cached = cache.get(source);
  if (cached) return cached;
  const ast = parse(source);
  cache.set(source, ast);
  return ast;
}

/**
 * Evaluate a `when` expression.
 *
 * @param source     the expression, or undefined (which means "always visible")
 * @param predicates host-computed booleans
 * @param scope      loop-scoped booleans (`part.last`, `ceremony.hasTime`, `category.active`, `gift.hasCover`)
 * @param strict     throw on an unknown identifier instead of treating it as false.
 *                   The importer validates in strict mode; the runtime does not,
 *                   so an engine upgrade can never blank a live page.
 */
export function evaluateWhen(
  source: string | undefined,
  predicates: Record<string, boolean>,
  scope: Record<string, unknown> = {},
  strict = false,
): boolean {
  if (!source) return true;

  const lookup = (name: string): boolean => {
    if (name in predicates) return predicates[name];
    if (name in scope) return Boolean(scope[name]);
    if (strict) throw new ExpressionError(`Unknown identifier '${name}' in expression: ${source}`);
    return false;
  };

  const walk = (node: Ast): boolean => {
    switch (node.n) {
      case "ident": return lookup(node.name);
      case "not": return !walk(node.operand);
      case "and": return walk(node.left) && walk(node.right);
      case "or": return walk(node.left) || walk(node.right);
    }
  };

  return walk(compile(source));
}

/** Identifiers referenced by an expression — used by the package validator. */
export function referencedIdentifiers(source: string): string[] {
  const names = new Set<string>();
  const walk = (node: Ast) => {
    switch (node.n) {
      case "ident": names.add(node.name); break;
      case "not": walk(node.operand); break;
      case "and":
      case "or": walk(node.left); walk(node.right); break;
    }
  };
  walk(compile(source));
  return [...names];
}
